import { createReducer } from "typesafe-actions";

import { Activity } from "@mapmycustomers/shared/types/entity";
import User, { UserRef } from "@mapmycustomers/shared/types/User";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import { EntityMention } from "@app/component/EntityMentions";

import {
  bumpTasksRefreshCounter,
  closeCoachingTaskEdit,
  CoachingTasksActions,
  CoachingTasksFilter,
  createCoachingTask,
  createCoachingTasksForTeam,
  deleteCoachingTask,
  fetchCoachingTasks,
  fetchLinkedTasks,
  fetchSuggestedActionOwner,
  injectCoachingTask,
  openCoachingTaskEdit,
  setCoachingTaskMentions,
  toggleCoachingTaskComplete,
  updateCoachingTask,
} from "./actions";

// Helper: returns true if a task's completed state belongs in the currently selected filter view.
const matchesFilter = (completed: boolean, filter: CoachingTasksFilter): boolean => {
  if (filter === "all") {
    return true;
  }
  return filter === "active" ? !completed : completed;
};

export interface CoachingTasksState {
  createLoading: boolean;
  // Mirrors the panel's current Active/Completed/All toggle so optimistic
  currentFilter: CoachingTasksFilter;
  currentTabUserId: null | User["id"];
  deleteLoadingIds: Activity["id"][];
  editingTaskId: Activity["id"] | null;
  // Resolved by id but doesn't match currentFilter — kept out of tasks[].
  editingTaskOutOfFilter: Activity | null;
  linkedTasksById: Record<Activity["id"], Activity>;
  loading: boolean;
  loadingMore: boolean;
  // In-session mention seeds keyed by task id.
  mentionsByTaskId: Record<Activity["id"], EntityMention[]>;
  // Tactical event-bus counter — see notes/reducers.ts for the trade-off
  refreshCounter: number;
  suggestedActionOwner: null | UserRef;
  suggestedActionOwnerLoading: boolean;
  tasks: Activity[];
  toggleCompleteLoadingIds: Activity["id"][];
  total: number;
  updateLoadingIds: Activity["id"][];
}

export const initialCoachingTasksState: CoachingTasksState = {
  createLoading: false,
  currentFilter: "active",
  currentTabUserId: null,
  deleteLoadingIds: [],
  editingTaskId: null,
  editingTaskOutOfFilter: null,
  linkedTasksById: {},
  loading: false,
  loadingMore: false,
  mentionsByTaskId: {},
  refreshCounter: 0,
  suggestedActionOwner: null,
  suggestedActionOwnerLoading: false,
  tasks: [],
  toggleCompleteLoadingIds: [],
  total: 0,
  updateLoadingIds: [],
};

const coachingTasks = createReducer<CoachingTasksState, CoachingTasksActions>(
  initialCoachingTasksState
)
  .handleAction(fetchCoachingTasks.request, (state, { payload }) => ({
    ...state,
    currentFilter: payload.silent ? state.currentFilter : payload.filter,
    currentTabUserId: payload.tabUserId,
    loading: payload.silent ? state.loading : !payload.append,
    loadingMore: payload.silent ? state.loadingMore : !!payload.append,
  }))
  .handleAction(fetchCoachingTasks.success, (state, { payload }) => {
    let tasks: Activity[];
    if (payload.append) {
      tasks = [...state.tasks, ...payload.tasks];
    } else {
      // Non-append fetch — initial load, a filter/sort change, or a silent poll.
      // The panel fetches limit = loaded count, so for a silent poll this is an
      // authoritative re-sync of the loaded window: the fresh page replaces the
      // list, so edits AND deletes reflect in every loaded state, not only when
      // the list is fully loaded (a union-merge could only add, never drop a
      // deleted task). Tasks support dueDate/updatedAt sorts with a nullable due
      // date, so the notes window-floor trick (keep the bumped tail, drop the
      // deleted) doesn't transfer cleanly; the only cost of a plain replace is
      // that a new task entering the window slides the bottom-most loaded row
      // out of view until the next "Load more" — acceptable for a task list.
      tasks = payload.tasks;
    }
    return {
      ...state,
      loading: false,
      loadingMore: false,
      tasks,
      total: payload.total,
    };
  })
  .handleAction(fetchCoachingTasks.failure, (state) => ({
    ...state,
    loading: false,
    loadingMore: false,
  }))
  .handleAction(fetchLinkedTasks.success, (state, { payload }) => ({
    ...state,
    linkedTasksById: payload.reduce<Record<Activity["id"], Activity>>(
      (acc, task) => {
        acc[task.id] = task;
        return acc;
      },
      { ...state.linkedTasksById }
    ),
  }))
  .handleAction(createCoachingTask.request, (state) => ({
    ...state,
    createLoading: true,
  }))
  // Bump total + prepend only when the new task matches the current filter
  .handleAction(createCoachingTask.success, (state, { payload }) => {
    const visibleInCurrentFilter = matchesFilter(!!payload.completed, state.currentFilter);
    const belongsToCurrentTab =
      isNotEmpty(state.currentTabUserId) && payload.assignee?.id === state.currentTabUserId;
    const shouldAdd = visibleInCurrentFilter && belongsToCurrentTab;
    return {
      ...state,
      createLoading: false,
      tasks: shouldAdd ? [payload, ...state.tasks] : state.tasks,
      total: shouldAdd ? state.total + 1 : state.total,
    };
  })
  .handleAction(createCoachingTask.failure, (state) => ({
    ...state,
    createLoading: false,
  }))
  // Team tasks land in each rep's own pool, not the manager's open tab, so the
  // list isn't touched — only the shared creating flag is toggled.
  .handleAction(createCoachingTasksForTeam.request, (state) => ({
    ...state,
    createLoading: true,
  }))
  .handleAction(createCoachingTasksForTeam.success, (state) => ({
    ...state,
    createLoading: false,
  }))
  .handleAction(createCoachingTasksForTeam.failure, (state) => ({
    ...state,
    createLoading: false,
  }))
  .handleAction(toggleCoachingTaskComplete.request, (state, { payload }) => ({
    ...state,
    toggleCompleteLoadingIds: [...state.toggleCompleteLoadingIds, payload.id],
  }))
  // Evict + decrement when toggle flips the task out of the current filter.
  .handleAction(toggleCoachingTaskComplete.success, (state, { payload }) => {
    const stillVisible = matchesFilter(!!payload.completed, state.currentFilter);
    return {
      ...state,
      linkedTasksById: isNotEmpty(state.linkedTasksById[payload.id])
        ? { ...state.linkedTasksById, [payload.id]: payload }
        : state.linkedTasksById,
      tasks: stillVisible
        ? state.tasks.map((task) => (task.id === payload.id ? payload : task))
        : state.tasks.filter((task) => task.id !== payload.id),
      toggleCompleteLoadingIds: state.toggleCompleteLoadingIds.filter((id) => id !== payload.id),
      total: stillVisible ? state.total : Math.max(0, state.total - 1),
    };
  })
  .handleAction(toggleCoachingTaskComplete.failure, (state, { payload }) => ({
    ...state,
    toggleCompleteLoadingIds: state.toggleCompleteLoadingIds.filter((id) => id !== payload),
  }))
  .handleAction(deleteCoachingTask.request, (state, { payload }) => ({
    ...state,
    deleteLoadingIds: [...state.deleteLoadingIds, payload.id],
  }))
  .handleAction(deleteCoachingTask.success, (state, { payload }) => {
    const { [payload.id]: _dropped, ...mentionsByTaskId } = state.mentionsByTaskId;
    const { [payload.id]: _droppedLinked, ...linkedTasksById } = state.linkedTasksById;
    return {
      ...state,
      deleteLoadingIds: state.deleteLoadingIds.filter((id) => id !== payload.id),
      linkedTasksById,
      mentionsByTaskId,
      tasks: state.tasks.filter((task) => task.id !== payload.id),
      total: Math.max(0, state.total - 1),
    };
  })
  .handleAction(deleteCoachingTask.failure, (state, { payload }) => ({
    ...state,
    deleteLoadingIds: state.deleteLoadingIds.filter((id) => id !== payload),
  }))
  .handleAction(updateCoachingTask.request, (state, { payload }) => ({
    ...state,
    updateLoadingIds: [...state.updateLoadingIds, payload.activity.id],
  }))
  .handleAction(updateCoachingTask.success, (state, { payload }) => ({
    ...state,
    linkedTasksById: isNotEmpty(state.linkedTasksById[payload.id])
      ? { ...state.linkedTasksById, [payload.id]: payload }
      : state.linkedTasksById,
    tasks: state.tasks.map((task) => (task.id === payload.id ? payload : task)),
    updateLoadingIds: state.updateLoadingIds.filter((id) => id !== payload.id),
  }))
  .handleAction(updateCoachingTask.failure, (state, { payload }) => ({
    ...state,
    updateLoadingIds: state.updateLoadingIds.filter((id) => id !== payload),
  }))
  .handleAction(bumpTasksRefreshCounter, (state) => ({
    ...state,
    refreshCounter: state.refreshCounter + 1,
  }))
  .handleAction(fetchSuggestedActionOwner.request, (state) => ({
    ...state,
    suggestedActionOwner: null,
    suggestedActionOwnerLoading: true,
  }))
  .handleAction(fetchSuggestedActionOwner.success, (state, { payload }) => ({
    ...state,
    suggestedActionOwner: payload,
    suggestedActionOwnerLoading: false,
  }))
  .handleAction(fetchSuggestedActionOwner.failure, (state) => ({
    ...state,
    suggestedActionOwnerLoading: false,
  }))
  .handleAction(setCoachingTaskMentions, (state, { payload }) => ({
    ...state,
    mentionsByTaskId: { ...state.mentionsByTaskId, [payload.taskId]: payload.mentions },
  }))
  // Edit modal lifecycle.
  .handleAction(openCoachingTaskEdit, (state, { payload }) => ({
    ...state,
    editingTaskId: payload,
  }))
  .handleAction(closeCoachingTaskEdit, (state) => ({
    ...state,
    editingTaskId: null,
    editingTaskOutOfFilter: null,
  }))
  .handleAction(injectCoachingTask, (state, { payload }) => {
    const inList = state.tasks.some((task) => task.id === payload.id);
    if (inList) {
      return {
        ...state,
        linkedTasksById: isNotEmpty(state.linkedTasksById[payload.id])
          ? { ...state.linkedTasksById, [payload.id]: payload }
          : state.linkedTasksById,
        tasks: state.tasks.map((task) => (task.id === payload.id ? payload : task)),
      };
    }
    const matchesAssignee =
      isNotEmpty(state.currentTabUserId) && payload.assignee?.id === state.currentTabUserId;
    if (!matchesFilter(!!payload.completed, state.currentFilter) || !matchesAssignee) {
      return {
        ...state,
        editingTaskOutOfFilter: payload,
        linkedTasksById: { ...state.linkedTasksById, [payload.id]: payload },
      };
    }
    return { ...state, tasks: [payload, ...state.tasks] };
  });

export default coachingTasks;
